import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { ApiError, ERROR_CODES } from '@/services/apiError'
import { fetchRoles } from '@/features/rbac/api'
import { fetchPatterns } from '@/features/shiftPatterns/api'
import { asUtc } from '@/lib/format'

/**
 * The account directory.
 *
 * ── An invitation, not a create ──────────────────────────────────────────
 * `POST /users` stores a person with NO password and emails them a link. Nobody
 * is given credentials here and no account starts out usable, which is why the
 * form says "Invite" and why there is no password field on it. The row that
 * appears is real and cannot sign in yet — `lastActiveAt` is `null` and stays
 * null until they accept.
 *
 * The demo screen had no Create at all, on the reasoning that "an invitation
 * goes to an address and comes back accepted or not, nothing here can send one".
 * The API can send one. So it exists now, and it is spelled as what it does.
 *
 * ── Root users are listed and nothing else ───────────────────────────────
 * `GET /users` includes them; `GET/PUT/DELETE /users/{id}` all refuse one with a
 * 403. A root account is managed through `/users/me` by the person themselves.
 * `isRoot` is on every row so the table can decline to offer what the server
 * would refuse, rather than offering it and explaining afterwards.
 *
 * ── The roles payload is exact, and the server checks four things ────────
 * This is the part worth reading before building a form over it. For each role
 * granted to a person the API wants:
 *
 *   1. every permission the ROLE holds, all of them, no others. A subset is a
 *      422 — you cannot grant somebody two thirds of a role.
 *   2. at least one scope per permission, each a single hierarchy node, at a
 *      level that permission's `targetLevels` allows.
 *   3. the role's own `scopes` equal to the UNION of its permissions' scopes.
 *   4. no duplicate role or permission ids — merge scopes into one entry.
 *
 * (3) is why nothing here asks a caller for role scopes: `toRoleGrant` derives
 * them. Two sources for one fact is how a 422 nobody can read gets produced.
 *
 * ── Scope is per grant, not per role ─────────────────────────────────────
 * A role is a bundle of capabilities; WHERE somebody may exercise them is
 * decided when they are given it. So "Maintenance Technician at Findlay" is one
 * person's grant and not a second role, and the same role granted to somebody
 * else at Pune is the same role.
 */

/**
 * Which `ScopeIn` key each hierarchy tier is spelled with, and which
 * `TargetLevel` the server calls it.
 *
 * Three vocabularies for four levels, and they do not agree: the frontend
 * hierarchy says `site`/`asset`, the scope columns say `plant_id`/`machine_id`,
 * and `target_level` says `plant`/`machine`. Mapped in one table so the
 * disagreement is stated once instead of guessed at three call sites.
 *
 * ── Four, not six, and the two that went were load-bearing here ─────────
 * `ScopeIn` declares exactly `account_id`, `plant_id`, `line_id`, `machine_id`,
 * and its validator demands exactly one of them be set. Region and sub-region
 * were dropped from the table, the columns and the `TargetLevel` enum by
 * migration `f915c8d4b697`.
 *
 * Leaving them listed here was not a harmless extra: `toScopeIn` sends EVERY
 * field in this table with the unused ones null, and the server validates every
 * body with `extra="forbid"`. So a stale row here is not an ignored key, it is
 * a 400 naming `region_id` on every grant write the user editor makes.
 */
export const SCOPE_TIERS = [
  { tier: 'enterprise', field: 'account_id', level: 'account' },
  { tier: 'site', field: 'plant_id', level: 'plant' },
  { tier: 'line', field: 'line_id', level: 'line' },
  { tier: 'asset', field: 'machine_id', level: 'machine' },
]

const TIER_BY_LEVEL = new Map(SCOPE_TIERS.map((entry) => [entry.level, entry.tier]))
const FIELD_BY_TIER = new Map(SCOPE_TIERS.map((entry) => [entry.tier, entry.field]))

/** `TargetLevel` → the tier the hierarchy calls it. Used to filter the picker. */
export const tierForLevel = (level) => TIER_BY_LEVEL.get(level) ?? null

/**
 * Wire → app, for one scope row.
 *
 * Exactly one of the four columns is set — the server validates that, so finding
 * the set one is a lookup and not a decision. `null` would mean a row with none
 * of them set, which cannot be stored.
 */
const toScope = (row) => {
  for (const { field, tier } of SCOPE_TIERS) {
    if (row?.[field]) return { tier, id: row[field] }
  }
  return null
}

/** App → wire. One node becomes the one column that names its level. */
const toScopeIn = ({ tier, id }) => {
  const field = FIELD_BY_TIER.get(tier)
  if (!field) return null
  /* Every key present, one of them set. A `ScopeIn` with a key missing is a 422
     rather than a default — the model declares all four as `| None` with no
     default, and nullable here means required. A key it does NOT declare is a
     400, so this table must name the columns exactly. */
  return SCOPE_TIERS.reduce(
    (out, entry) => ({ ...out, [entry.field]: entry.field === field ? id : null }),
    {},
  )
}

/** A scope's identity, for de-duplicating a union. One node at one level. */
const scopeKey = ({ tier, id }) => `${tier}:${id}`

const toPermissionGrant = (row) => ({
  permissionId: row.permission_id,
  scopes: (row.scopes ?? []).map(toScope).filter(Boolean),
})

const toRoleGrant = (row) => ({
  roleId: row.role_id,
  permissions: (row.permissions ?? []).map(toPermissionGrant),
  /* Read back but never sent from here — see the header, rule (3). Kept on the
     record because an editor opening an existing grant should be able to show
     what the server holds rather than only what it would recompute. */
  scopes: (row.scopes ?? []).map(toScope).filter(Boolean),
})

/**
 * App → wire, for one role grant.
 *
 * Takes the per-permission scopes and derives the role's own, which is rule (3)
 * satisfied by construction rather than by the caller remembering it.
 *
 * @param {{roleId: string, permissions: Array<{permissionId: string, scopes: Array<{tier: string, id: string}>}>}} grant
 */
const toRoleGrantIn = (grant) => {
  const union = new Map()
  for (const permission of grant.permissions) {
    for (const scope of permission.scopes) union.set(scopeKey(scope), scope)
  }

  return {
    role_id: grant.roleId,
    scopes: [...union.values()].map(toScopeIn).filter(Boolean),
    permissions: grant.permissions.map((permission) => ({
      permission_id: permission.permissionId,
      scopes: permission.scopes.map(toScopeIn).filter(Boolean),
    })),
  }
}

const toShiftAssignment = (row) => ({
  shiftId: row.shift_id,
  startDate: row.start_date,
  endDate: row.end_date ?? null,
})

/** Wire → app, for a row in the table. */
const toListItem = (row) => ({
  id: row.id,
  firstName: (row.first_name ?? '').trim(),
  lastName: (row.last_name ?? '').trim(),
  name: [row.first_name, row.last_name].map((part) => (part ?? '').trim()).filter(Boolean).join(' '),
  email: row.email ?? '',
  isActive: row.is_active === true,
  /* Listed, not manageable. See the header. */
  isRoot: row.is_root === true,
  /* `null` is a real answer and the useful one: an invited account nobody has
     accepted is exactly what an access review is looking for, and it is the same
     column that says so. */
  lastActiveAt: asUtc(row.last_active_at),
  createdAt: asUtc(row.created_at),
})

/** Initials for the avatar, derived rather than stored. */
export const initialsOf = (person) =>
  [person.firstName, person.lastName]
    .map((part) => (part ?? '').trim().charAt(0))
    .filter(Boolean)
    .join('')
    .toUpperCase() || '—'

const toDetail = (row) => ({
  ...toListItem(row),
  /* Detail refuses a root user outright, so anything read here is manageable.
     `is_root` is absent from `UserDetailOut` for that reason — `toListItem`
     reads it as false, which is correct for every row that reaches this point. */
  avatarFileId: row.avatar_file_id ?? null,
  avatarUrl: row.avatar_view_url ?? null,
  roles: (row.roles ?? []).map(toRoleGrant),
  shifts: (row.shifts ?? []).map(toShiftAssignment),
})

/**
 * The directory, one page of it.
 *
 * `search` and the sort are the SERVER's: the client holds one page, so
 * filtering locally would search what happens to be on screen and quietly
 * report nothing on page two. The same reasoning the hierarchy's child lists
 * settled on.
 *
 * `counts` is over the whole filtered set rather than the page, which is what
 * makes the two tiles above the table a count rather than a page tally. Note
 * they respect `search` and not `is_active` — the server applies the search
 * first and counts both sides of the active split after it — so the tiles narrow
 * with a query and do not move when the filter chooses between them.
 *
 * Sort is `created_at` or `first_name` and nothing else. Not `last_active_at`,
 * which is the column an access review would actually sort by: see
 * BACKEND-GAPS.md.
 */
export async function fetchUsers({ isActive, search, sortBy, sortDir, page, pageSize } = {}, signal) {
  const data = await get(endpoints.users.list, {
    params: {
      page: page ?? 1,
      page_size: pageSize ?? 50,
      sort_by: sortBy ?? 'first_name',
      sort_dir: sortDir ?? 'asc',
      /* Omitted rather than sent empty. `is_active` is a tri-state on the wire —
         absent means both — and `search=` would be a search for the empty
         string. */
      ...(typeof isActive === 'boolean' ? { is_active: isActive } : {}),
      ...(search ? { search } : {}),
    },
    signal,
  })

  return {
    items: (data?.users ?? []).map(toListItem),
    page: data?.page ?? 1,
    pageSize: data?.page_size ?? 50,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    counts: {
      active: data?.counts?.is_active?.true ?? 0,
      inactive: data?.counts?.is_active?.false ?? 0,
    },
  }
}

/**
 * One person, with their grants.
 *
 * The list carries none of this, so the editor reads the person it is opening —
 * the same shape the hierarchy's node forms use, and for the same reason: a
 * full-replace PUT has to send back everything it did not change.
 */
export async function fetchUser(id, signal) {
  return toDetail(await get(endpoints.users.detail(id), { signal }))
}

const toShiftAssignmentIn = (assignment) => ({
  shift_id: assignment.shiftId,
  start_date: assignment.startDate,
  end_date: assignment.endDate ?? null,
})

/**
 * Invite one.
 *
 * `roles` and `shifts` are both required KEYS and may both be empty arrays. An
 * empty `roles` creates somebody who can sign in and see nothing, which is
 * allowed on purpose: granting access is a second decision and does not have to
 * be taken in the same minute as creating the account. An empty `shifts` is not
 * even that — plenty of people work no pattern at all.
 *
 * A shift assignment is `{shiftId, startDate, endDate}`, where `endDate: null`
 * means "until further notice" and is a REAL value rather than an omission —
 * the key must be present either way. `shift_id` is verified against the
 * account's live patterns, and duplicates are refused: one entry per shift, with
 * the dates merged into it.
 *
 * Refusals come back field-keyed: `email` for an address already in the account,
 * and `roles`/`permissions`/`scopes` for any of the four rules in the header.
 *
 * @param {{firstName: string, lastName: string, email: string, roles?: Array, shifts?: Array}} input
 */
export async function createUser({ firstName, lastName, email, roles = [], shifts = [] }) {
  const row = await post(endpoints.users.list, {
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    /* Trimmed because a pasted address routinely carries whitespace. Not
       lower-cased: the server compares addresses exactly, and the local part of
       an address is case-sensitive by spec. */
    email: email.trim(),
    roles: roles.map(toRoleGrantIn),
    shifts: shifts.map(toShiftAssignmentIn),
  })
  return toDetail(row)
}

/**
 * Update one.
 *
 * Full replace, and `UserUpdate` is NOT `UserCreate` — two differences, both
 * load-bearing:
 *
 *   `email`     — absent. A login address is changed by its owner through
 *                 `/users/me`, which sends a confirmation link to the new
 *                 address and only switches it once that is clicked. An admin
 *                 cannot reassign somebody's login here, which is right.
 *   `is_active` — present, and the only way to suspend an account.
 *
 * `roles` and `shifts` replace what is there: a role left out of the payload is
 * revoked, along with its permissions and their scopes, in one cascade, and a
 * shift left out is unassigned. So the form must send the whole set back, which
 * is why the editor reads the person first rather than patching from a table
 * row.
 *
 * @param {{id: string, firstName: string, lastName: string, isActive: boolean, roles?: Array, shifts?: Array}} input
 */
export async function updateUser({ id, firstName, lastName, isActive, roles = [], shifts = [] }) {
  const row = await put(endpoints.users.detail(id), {
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    is_active: isActive,
    roles: roles.map(toRoleGrantIn),
    shifts: shifts.map(toShiftAssignmentIn),
  })
  return toDetail(row)
}

/**
 * A person's grants, reconciled against the roles as they stand now.
 *
 * Not a straight echo of what the server holds, and the difference is
 * load-bearing. `_validate_roles_payload` demands that the permissions sent for
 * a role match the role's own EXACTLY — but editing a role's permissions does
 * not reconcile the grants people already hold. So a grant stored before that
 * edit is unsendable, and the PUT fails on a change that had nothing to do with
 * it. Renaming somebody 422s.
 *
 * This is therefore a client papering over a server-side inconsistency, and it
 * is recorded as one in BACKEND-GAPS.md. What it does:
 *
 *   - a permission the role has GAINED is added with no scopes. Unsendable
 *     until something gives it one, which is correct — there is no scope to
 *     infer, and guessing one would grant access nobody chose.
 *   - a permission the role has LOST is dropped, with its scopes.
 *   - a grant for a role that no longer exists is dropped whole. It could not be
 *     sent back either way: `roles` is verified against the visible set.
 *
 * @param {Array} grants         `person.roles`
 * @param {Map} roleById         id → role, from the roles list
 */
export function reconcileGrants(grants, roleById) {
  const out = []

  for (const grant of grants ?? []) {
    const role = roleById.get(grant.roleId)
    if (!role) continue

    const heldScopes = new Map(
      grant.permissions.map((permission) => [permission.permissionId, permission.scopes]),
    )

    out.push({
      roleId: grant.roleId,
      permissions: role.permissionIds.map((permissionId) => ({
        permissionId,
        scopes: heldScopes.get(permissionId) ?? [],
      })),
    })
  }

  return out
}

/**
 * A person's shift assignments, minus any whose pattern is gone.
 *
 * Deleting a shift pattern soft-deletes the pattern and leaves the `UserShift`
 * rows pointing at it. `GET /users/{id}` still returns those assignments, and
 * `_validate_shifts_payload` then refuses them on the way back in — so a
 * deleted pattern makes every subsequent write for that person fail, on a save
 * that had nothing to do with shifts. Exactly the shape of the role-drift
 * problem above, and recorded beside it in BACKEND-GAPS.md.
 *
 * Dropped rather than surfaced: the assignment cannot be sent, the pattern it
 * named no longer exists to reinstate, and there is nothing a reader could do
 * about it on a Deactivate.
 */
export const liveAssignments = (assignments, shiftIds) =>
  (assignments ?? []).filter((entry) => shiftIds.has(entry.shiftId))

/** Whether every permission in a reconciled grant set has somewhere to apply. */
export const isSendable = (grants) =>
  grants.every((grant) => grant.permissions.every((permission) => permission.scopes.length > 0))

/**
 * Suspend or reinstate, without opening the editor.
 *
 * Sends the person's shift assignments back for the same reason it sends their
 * grants back: `shifts` is full-replace, so omitting it would unassign somebody
 * from every pattern as a side effect of deactivating them. Reconciled first —
 * see `liveAssignments`, without which deleting a pattern would make every
 * person on it impossible to deactivate.
 *
 * There is no PATCH: `is_active` travels on the full-replace PUT, so changing it
 * alone still means sending everything else back unchanged. Hence the read
 * first — a PUT built from a table row would send `roles: []` and silently
 * revoke every grant the person holds.
 *
 * It reads the ROLES as well, and reconciles, for the reason above: without it
 * this would refuse exactly when a role has been edited since the grant, which
 * is both the least obvious moment and the one nobody would connect to a
 * Deactivate button. When reconciliation leaves a permission with no scope there
 * is nothing this can safely send, so it throws rather than guessing — and the
 * caller's message says to open the editor, which is where a scope can be
 * chosen.
 *
 * The demo screen offered three states. There are two: `is_active` is a boolean,
 * and a "locked" account — one the server has shut out after failed sign-ins —
 * is not a thing this API records. See BACKEND-GAPS.md.
 */
export async function setUserActive({ id, isActive }) {
  const [current, roleList, patterns] = await Promise.all([
    fetchUser(id),
    fetchRoles(),
    fetchPatterns(),
  ])
  const roleById = new Map(roleList.items.map((role) => [role.id, role]))
  const roles = reconcileGrants(current.roles, roleById)
  const shifts = liveAssignments(current.shifts, new Set(patterns.items.map((s) => s.id)))

  if (!isSendable(roles)) {
    throw new ApiError({
      code: ERROR_CODES.VALIDATION,
      detail: 'A role has gained a permission with no place to apply it.',
      fields: { scopes: 'Open the editor to choose where it applies.' },
    })
  }

  return updateUser({
    id,
    firstName: current.firstName,
    lastName: current.lastName,
    isActive,
    roles,
    shifts,
  })
}

/**
 * Delete one.
 *
 * Soft on the server — the row is stamped, not removed — but there is nothing
 * here that can undo it, so the dialog says what it means rather than promising
 * a recovery no screen offers.
 *
 * Refused for a root user. Not refused for somebody who owns work: unlike a
 * role, a person with tasks and logbook entries against their name deletes
 * cleanly, and those records keep pointing at a user that no longer lists. See
 * BACKEND-GAPS.md.
 */
export async function deleteUser(id) {
  await del(endpoints.users.detail(id))
  return { id, deleted: true }
}

/**
 * Set a password from an emailed token. PUBLIC.
 *
 * One endpoint for two journeys, which is the server's design and not a
 * shortcut: an invited user setting their first password and an existing user
 * resetting a forgotten one both carry a `set_password` token, and the server
 * tells them apart by whether a password was already stored. It notifies on a
 * reset and stays quiet on a first set.
 *
 * No Authorization header is needed and none is sent — there is no session yet.
 * The token is the credential, it expires, and `token` is the field a refusal
 * comes back under.
 */
export async function setPassword({ token, password }) {
  // Never trimmed. A leading or trailing space in a password can be deliberate.
  await post(endpoints.users.setPassword, { token, password })
  return { ok: true }
}

/**
 * Confirm a new email address from an emailed token. PUBLIC.
 *
 * Reached only from the link `PUT /users/me` sends when somebody changes their
 * own address. Until it is clicked the account still signs in with the OLD
 * address — the change is pending, not applied, which is worth saying on the
 * screen because a profile form that already shows the new one implies
 * otherwise.
 *
 * Refusals: `token` for expired or already-used, `email` for an address that was
 * taken by somebody else in the meantime.
 */
export async function confirmEmail({ token }) {
  const row = await post(endpoints.users.confirmEmail, { token })
  return { email: row?.email ?? null }
}
